import {FC, useEffect, useState} from 'react'
import MoreButton from '_core/components/MoreButton'
import {Dropdown} from 'react-bootstrap'
import {financialStatusTypes, getStoreLogoUrl} from 'app/utilities'
import ordersService from 'app/services/OrdersService'
import {isOrderPaymentFailed} from '../../models/EditOrderState'
import {formatToDateString2} from 'app/format/date'
import {simpleCurrency2} from 'app/format/currency'
import {generatePDF} from 'app/utilities/pdf'
import {useDispatch} from 'react-redux'
import {hideLoader, showLoader} from 'app/redux/loaderSlice'

interface Props {
  order?: any
}

export const PrintOrderInvoice: FC<Props> = ({order}) => {
  const dispatch = useDispatch()
  const [store, setStore] = useState<any>(null)

  useEffect(() => {
    const fetch = async () => {
      const response = await ordersService.store()
      const store = {
        ...response,
        storeLogoUrl: getStoreLogoUrl({id: response.id, storeLogo: response.storeLogo}),
      }
      setStore(store)
    }
    fetch()
  }, [])

  const handlePrint = async () => {
    try {
      dispatch(showLoader())
      const pdf = await ordersService.printOrderItems(mapToModel({...order, store}))
      const name = `invoice_${order.invoiceNumber}`
      generatePDF(pdf, name)
      dispatch(hideLoader())
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(hideLoader())
    }
  }

  if (
    isOrderPaymentFailed(order.financialStatus) ||
    order.financialStatus === financialStatusTypes.refunded ||
    !store
  ) {
    return null
  }

  return (
    <MoreButton>
      <Dropdown.Item eventKey='1' onClick={handlePrint}>
        <div className='text-hover-primary'>PDF Print/Download Invoice</div>
      </Dropdown.Item>
    </MoreButton>
  )
}

export default PrintOrderInvoice

const mapToModel = (order: any) => {
  const model = {
    name: order.name,
    email: order.email,
    totalPrice: simpleCurrency2(order.totalPrice),
    subtotalPrice: simpleCurrency2(order.subtotalPrice),
    orderDate: formatToDateString2(order.createdAt),
    shippingPrice: simpleCurrency2(order.shippingPrice),
    totalDiscounts: simpleCurrency2(
      (parseFloat(order.totalDiscounts) + parseFloat(order.wineClubDiscounts)) * -1
    ),
    totalTax: simpleCurrency2(order.totalTax),
    invoiceNumber: order.invoiceNumber,
    totalAmountPaid: simpleCurrency2(order.totalAmountPaid),
    totalTip: simpleCurrency2(order.tipAmount),
    customer: {
      firstName: order.customer.firstName || '',
      lastName: order.customer.lastName || '',
      email: order.customer.email || '',
      phone: order.customer.phone || '',
      billingAddress: order.customer?.billingAddress
        ? formatAddress(order.customer?.billingAddress)
        : null,
      shippingAddress: order.customer?.shippingAddress
        ? formatAddress(order.customer?.shippingAddress)
        : null,
    },
    orderItems: order.orderItems.map((item: any) => ({
      price: simpleCurrency2(item.price),
      quantity: item.quantity - item.refundedQuantity,
      itemPrice: simpleCurrency2(item.variant?.price),
      productName: item.variant.product.name,
      variantName: item.variant.name,
      itemSubtotal: simpleCurrency2(
        (item.quantity - item.refundedQuantity) * parseFloat(item.price)
      ),
    })),
    store: {
      name: order.store.name,
      email: order.store.email,
      addressLine1: order.store.addressLine1,
      addressLine2: order.store.addressLine2 || null,
      city: order.store.city || null,
      state: order.store.state?.name || null,
      country: order.store.country?.code || null,
      zipCode: order.store.zipCode,
      storeLogoUrl: order.store.storeLogoUrl || null,
      storeUrl: order.store?.storeUrl
        ? `https://${order.store.storeUrl}`
        : `https://${order.store.domain}.corksy.io`,
    },
  }

  return model
}

const formatAddress = (address: any) => ({
  firstName: address?.firstName || null,
  lastName: address?.lastName || null,
  addressLine1: address?.addressLine1 || null,
  addressLine2: address?.addressLine2 || null,
  city: address?.city || null,
  state: address?.state?.name || null,
  country: address?.country?.code || null,
  zipCode: address?.zipCode || null,
})
